import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ActionButton, CloseWrap, IconWrap, NotificationWrap, Title, TopHead } from './styled';
import Icon from './Icon';

import { ReactComponent as CloseIcon } from '@assets/icons/close-notification.svg';
import { Notification as NotificationInterface, NotificationType } from '@store/notifications/types';
import { removeNotificationActionCreator } from '@store/notifications/actions';
import { NOTIFICATIONS_DELAY, NOTIFICATIONS_DURATION } from '@utils/config';
import { getOperationUuid } from '@utils/localStorage';
import { Subtitle } from '@components/Typography';

interface Props {
  notification: NotificationInterface;
}

const delay = NOTIFICATIONS_DURATION;
const animationDuration = NOTIFICATIONS_DELAY;

const Notification: React.FunctionComponent<Props> = ({ notification }) => {
  const { t } = useTranslation();
  const visibilityTimeout = useRef<NodeJS.Timeout>();
  const removalTimeout = useRef<NodeJS.Timeout>();
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(true);
  const operationId = getOperationUuid();

  const remove = useCallback(
    (immediate?: boolean) => {
      visibilityTimeout.current = setTimeout(
        () => {
          setIsVisible(false);
        },
        immediate ? 0 : delay - animationDuration
      );

      removalTimeout.current = setTimeout(
        () => {
          if (notification.onClose) {
            notification.onClose();
          }
          dispatch(removeNotificationActionCreator(notification.id));
        },
        immediate ? animationDuration : delay
      );
    },
    [dispatch, notification]
  );

  useEffect(() => {
    remove();
  }, []);

  return (
    <NotificationWrap
      isVisible={isVisible}
      type={notification.type}
      animationDuration={animationDuration}
      onClick={async () => {
        clearTimeout(visibilityTimeout.current!);
        clearTimeout(removalTimeout.current!);
        remove(true);
      }}
    >
      <CloseWrap>
        <CloseIcon />
      </CloseWrap>
      <TopHead>
        <Title>
          <IconWrap>
            <Icon type={notification.type} />
          </IconWrap>
          {notification.title}
        </Title>
        {!!notification.action && (
          <ActionButton
            onClick={async () => {
              clearTimeout(visibilityTimeout.current!);
              clearTimeout(removalTimeout.current!);
              const isRemove = await notification.action?.onClick!();
              if (isRemove) remove(true);
            }}
          >
            {notification.action.title}
          </ActionButton>
        )}
      </TopHead>
      {notification?.type === NotificationType.Error && (
        <Subtitle size="small" style={{ marginLeft: '7%' }}>
          {t('validation.error.notification')}
          {operationId}
        </Subtitle>
      )}
    </NotificationWrap>
  );
};

export default Notification;
